/**
 * useAIColleague Hook
 *
 * Custom hook for chatting with AI expert colleagues.
 * Handles streaming responses and tool calls.
 *
 * Note: This is a custom implementation. For full AI SDK integration,
 * install `ai` package and use `useChat` from 'ai/react'.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { getEnv } from '@/config/env';

export type ColleagueId = 'alex' | 'morgan' | 'jordan' | 'sam' | 'riley' | 'taylor' | 'casey' | 'general';

const COLLEAGUE_IDS: ColleagueId[] = ['alex', 'morgan', 'jordan', 'sam', 'riley', 'taylor', 'casey', 'general'];
const HANDOFF_HEADER = 'x-ai-colleague-handoff';

export type ContextType =
  | 'case'
  | 'screening'
  | 'instruction'
  | 'ticket'
  | 'user'
  | 'solution'
  | 'card'
  | 'cardList'
  | 'dashboard'
  | 'feesPricing'
  | 'digitalDeposit'
  | 'digitalWithdraw'
  | 'digitalDepositQueue'
  | 'digitalWithdrawQueue';

/**
 * Attachment type for files/images in messages
 */
export type MessageAttachment = {
  /** Unique ID for the attachment */
  id: string;
  /** Original filename */
  name: string;
  /** MIME type (e.g., image/jpeg, application/pdf) */
  mimeType: string;
  /** File size in bytes */
  size: number;
  /** Base64-encoded file content */
  data: string;
  /** Preview URL for images (blob URL) */
  previewUrl?: string;
};

/**
 * Supported MIME types for multimodal AI processing
 */
export const SUPPORTED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf'
] as const;

export type HandoffSuggestion = {
  sourceColleagueId?: Exclude<ColleagueId, 'general'>;
  sourceName?: string;
  targetColleagueId: Exclude<ColleagueId, 'general'>;
  targetName: string;
  reason: string;
  originalQuestion: string;
  pendingLookupIntent?: string;
  extractedIdentifiers?: {
    last4?: string;
    refId?: string;
  };
  pageContext?: AIColleagueContext;
};

export type Message = {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string;
  createdAt: Date;
  /** Suggested switch when the current colleague is not the right owner */
  handoffSuggestion?: HandoffSuggestion;
  /** File attachments (images, PDFs) */
  attachments?: MessageAttachment[];
  toolCalls?: Array<{
    id: string;
    name: string;
    args: Record<string, unknown>;
  }>;
  toolResults?: Array<{
    id: string;
    name: string;
    result: unknown;
    error?: string;
  }>;
};

export type AIColleagueContext = {
  type?: ContextType;
  id?: string;
  data?: Record<string, unknown>;
  /** Friendly tab title from the tabs bar */
  tabTitle?: string;
  /** Specialist handoff metadata when continuing a routed lookup */
  handoff?: HandoffSuggestion;
};

export type UseAIColleagueOptions = {
  /** Initial colleague to chat with */
  colleagueId?: ColleagueId;
  /** Initial context */
  context?: AIColleagueContext;
  /** Initial messages */
  initialMessages?: Message[];
  /** API endpoint */
  api?: string;
  /** Called when response finishes */
  onFinish?: (message: Message) => void;
  /** Called on error */
  onError?: (error: Error) => void;
};

export type UseAIColleagueReturn = {
  /** Current messages */
  messages: Message[];
  /** Current input value */
  input: string;
  /** Set input value */
  setInput: (input: string) => void;
  /** Pending attachments to send with next message */
  attachments: MessageAttachment[];
  /** Add attachments */
  addAttachments: (files: File[]) => Promise<void>;
  /** Remove an attachment */
  removeAttachment: (id: string) => void;
  /** Clear all attachments */
  clearAttachments: () => void;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Send a message */
  sendMessage: (content?: string, contextOverride?: AIColleagueContext) => Promise<void>;
  /** Reload the last message */
  reload: () => Promise<void>;
  /** Stop streaming */
  stop: () => void;
  /** Clear messages */
  clearMessages: () => void;
  /** Set messages */
  setMessages: (messages: Message[]) => void;
  /** Current colleague */
  colleagueId: ColleagueId;
  /** Set colleague */
  setColleagueId: (id: ColleagueId) => void;
  /** Context */
  context: AIColleagueContext | undefined;
  /** Set context */
  setContext: (context: AIColleagueContext | undefined) => void;
  /** Ref for input element - attach to input for auto-focus after send */
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
};

/**
 * Generate a unique message ID
 */
const generateId = () => crypto.randomUUID();

/**
 * Parse streaming data chunks
 */
/**
 * Parse UI Message Stream chunks (AI SDK v5 format)
 * The stream sends SSE events with JSON payloads like:
 * data: {"type":"text-delta","delta":"Hello","id":"..."}
 */
function parseStreamChunk(chunk: string): { type: string; content?: string } | null {
  // First try AI SDK 4.x Data Stream format (most common)
  // Format: TYPE_CODE:JSON_VALUE (no space after colon)
  // 0 = text, 3 = text start, d = done, e = error
  if (chunk.startsWith('0:')) {
    try {
      const content = JSON.parse(chunk.slice(2));
      return { type: 'text', content };
    } catch {
      return null;
    }
  }
  if (chunk.startsWith('3:')) {
    // Text start marker, ignore
    return null;
  }
  if (chunk.startsWith('d:')) {
    return { type: 'done' };
  }
  if (chunk.startsWith('e:')) {
    try {
      const parsed = JSON.parse(chunk.slice(2));
      // If it contains an errorText field, it's a streaming error from the backend
      if (parsed.errorText) {
        return { type: 'error', content: parsed.errorText };
      }
    } catch {
      // Parse failure — treat as finish event
    }
    // Otherwise it's a finish event in AI SDK 4.x (finishReason and usage stats)
    return { type: 'done' };
  }
  // Other AI SDK 4.x stream types - ignore
  if (/^[0-9a-f]:/.test(chunk)) {
    return null;
  }

  // Then try SSE format (AI SDK v5/v6): "data: {...}"
  if (chunk.startsWith('data:')) {
    try {
      const jsonStr = chunk.slice(5).trim();
      if (!jsonStr || jsonStr === '[DONE]') return null;
      
      const parsed = JSON.parse(jsonStr);
      
      switch (parsed.type) {
        case 'text-delta':
          return { type: 'text', content: parsed.delta };
        case 'finish':
          return { type: 'done' };
        case 'error':
          return { type: 'error', content: parsed.errorText || 'Unknown error' };
        default:
          return null;
      }
    } catch {
      return null;
    }
  }

  return null;
}

function parseHandoffHeader(headers: Headers): HandoffSuggestion | undefined {
  const rawHeader = headers.get(HANDOFF_HEADER);
  if (!rawHeader) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(decodeURIComponent(rawHeader)) as Record<string, unknown>;
    const targetColleagueId = parsed.targetColleagueId;

    if (
      typeof targetColleagueId !== 'string' ||
      targetColleagueId === 'general' ||
      !COLLEAGUE_IDS.includes(targetColleagueId as ColleagueId) ||
      typeof parsed.targetName !== 'string' ||
      typeof parsed.reason !== 'string' ||
      typeof parsed.originalQuestion !== 'string'
    ) {
      return undefined;
    }

    const pageContext =
      parsed.pageContext && typeof parsed.pageContext === 'object'
        ? parsed.pageContext as AIColleagueContext
        : undefined;
    const extractedIdentifiers =
      parsed.extractedIdentifiers && typeof parsed.extractedIdentifiers === 'object'
        ? parsed.extractedIdentifiers as HandoffSuggestion['extractedIdentifiers']
        : undefined;

    return {
      sourceColleagueId: typeof parsed.sourceColleagueId === 'string'
        ? parsed.sourceColleagueId as Exclude<ColleagueId, 'general'>
        : undefined,
      sourceName: typeof parsed.sourceName === 'string' ? parsed.sourceName : undefined,
      targetColleagueId: targetColleagueId as Exclude<ColleagueId, 'general'>,
      targetName: parsed.targetName,
      reason: parsed.reason,
      originalQuestion: parsed.originalQuestion,
      pendingLookupIntent: typeof parsed.pendingLookupIntent === 'string' ? parsed.pendingLookupIntent : undefined,
      extractedIdentifiers,
      pageContext
    };
  } catch {
    return undefined;
  }
}

/**
 * Custom hook for AI colleague chat
 */
export function useAIColleague(options: UseAIColleagueOptions = {}): UseAIColleagueReturn {
  const {
    colleagueId: initialColleagueId = 'general',
    context: initialContext,
    initialMessages = [],
    api = '/api/ai/chat',
    onFinish,
    onError
  } = options;

  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState<MessageAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [colleagueId, setColleagueId] = useState<ColleagueId>(initialColleagueId);
  const [context, setContext] = useState<AIColleagueContext | undefined>(initialContext);

  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  /**
   * Convert a File to a MessageAttachment
   */
  const fileToAttachment = useCallback(async (file: File): Promise<MessageAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]; // Remove data:mime;base64, prefix
        resolve({
          id: generateId(),
          name: file.name,
          mimeType: file.type,
          size: file.size,
          data: base64,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
        });
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }, []);

  /**
   * Add file attachments
   */
  const addAttachments = useCallback(async (files: File[]) => {
    const validFiles = files.filter(file => 
      SUPPORTED_ATTACHMENT_TYPES.includes(file.type as typeof SUPPORTED_ATTACHMENT_TYPES[number])
    );
    
    if (validFiles.length !== files.length) {
      console.warn('Some files were filtered out - only images and PDFs are supported');
    }

    const newAttachments = await Promise.all(validFiles.map(fileToAttachment));
    setAttachments(prev => [...prev, ...newAttachments]);
  }, [fileToAttachment]);

  /**
   * Remove an attachment by ID
   */
  const removeAttachment = useCallback((id: string) => {
    setAttachments(prev => {
      const attachment = prev.find(a => a.id === id);
      if (attachment?.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
      return prev.filter(a => a.id !== id);
    });
  }, []);

  /**
   * Clear all attachments
   */
  const clearAttachments = useCallback(() => {
    setAttachments(prev => {
      prev.forEach(a => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      return [];
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (content?: string, contextOverride?: AIColleagueContext) => {
      const messageContent = content || input;
      if (!messageContent.trim() && attachments.length === 0) return;
      const requestContext = contextOverride ?? context;

      // Capture current attachments and clear them
      const messageAttachments = [...attachments];
      clearAttachments();

      // Clear input
      setInput('');
      setError(null);
      setIsLoading(true);

      // Add user message with attachments
      const userMessage: Message = {
        id: generateId(),
        role: 'user',
        content: messageContent,
        createdAt: new Date(),
        attachments: messageAttachments.length > 0 ? messageAttachments : undefined
      };

      setMessages((prev) => [...prev, userMessage]);

      // Prepare assistant message placeholder
      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: '',
        createdAt: new Date()
      };

      setMessages((prev) => [...prev, assistantMessage]);

      try {
        // Create abort controller
        abortControllerRef.current = new AbortController();

        // Prepare messages for API (include attachments for the current message)
        const apiMessages = [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
          // Only include attachments data for messages that have them
          attachments: m.attachments?.map(a => ({
            name: a.name,
            mimeType: a.mimeType,
            data: a.data
          }))
        }));

        // Send request
        const token = localStorage.getItem('token');
        const response = await fetch(`${getEnv('ADMIN_API_URL') || ''}${api}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
          },
          credentials: 'include',
          body: JSON.stringify({
            colleagueId,
            messages: apiMessages,
            context: requestContext
          }),
          signal: abortControllerRef.current.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const handoffSuggestion = parseHandoffHeader(response.headers);

        // Read the stream
        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let accumulatedContent = '';
        let pendingLine = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = (pendingLine + chunk).split('\n');
          pendingLine = lines.pop() ?? '';

          for (const line of lines) {
            if (!line) continue;

            const parsed = parseStreamChunk(line);
            if (parsed?.type === 'text' && parsed.content) {
              accumulatedContent += parsed.content;
              // Update message with accumulated content
              // Use flushSync to force immediate render for streaming effect
              flushSync(() => {
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastIndex = updated.length - 1;
                  if (updated[lastIndex]?.role === 'assistant') {
                    updated[lastIndex] = {
                      ...updated[lastIndex],
                      content: accumulatedContent
                    };
                  }
                  return updated;
                });
              });
            } else if (parsed?.type === 'error') {
              throw new Error(parsed.content);
            }
          }
        }

        const finalBufferedLine = pendingLine + decoder.decode();
        if (finalBufferedLine) {
          const parsed = parseStreamChunk(finalBufferedLine);
          if (parsed?.type === 'text' && parsed.content) {
            accumulatedContent += parsed.content;
            flushSync(() => {
              setMessages((prev) => {
                const updated = [...prev];
                const lastIndex = updated.length - 1;
                if (updated[lastIndex]?.role === 'assistant') {
                  updated[lastIndex] = {
                    ...updated[lastIndex],
                    content: accumulatedContent
                  };
                }
                return updated;
              });
            });
          } else if (parsed?.type === 'error') {
            throw new Error(parsed.content);
          }
        }

        // Final update
        const finalMessage: Message = {
          ...assistantMessage,
          content: accumulatedContent,
          handoffSuggestion
        };

        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = finalMessage;
          return updated;
        });

        onFinish?.(finalMessage);
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // Aborted, not an error
          return;
        }

        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        onError?.(error);

        // Remove the empty assistant message on error
        setMessages((prev) => prev.slice(0, -1));
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
        // Auto-focus input after send completes
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      }
    },
    [input, attachments, clearAttachments, messages, colleagueId, context, api, onFinish, onError]
  );

  const reload = useCallback(async () => {
    if (messages.length < 2) return;

    // Get the last user message
    const lastUserMessage = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUserMessage) return;

    // Remove the last assistant and user messages
    setMessages((prev) => {
      const filtered = [...prev];
      // Find and remove last assistant message
      for (let i = filtered.length - 1; i >= 0; i--) {
        if (filtered[i].role === 'assistant') {
          filtered.splice(i, 1);
          break;
        }
      }
      // Find and remove last user message
      for (let i = filtered.length - 1; i >= 0; i--) {
        if (filtered[i].role === 'user') {
          filtered.splice(i, 1);
          break;
        }
      }
      return filtered;
    });

    // Resend
    await sendMessage(lastUserMessage.content);
  }, [messages, sendMessage]);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    input,
    setInput,
    attachments,
    addAttachments,
    removeAttachment,
    clearAttachments,
    isLoading,
    error,
    sendMessage,
    reload,
    stop,
    clearMessages,
    setMessages,
    colleagueId,
    setColleagueId,
    context,
    setContext,
    inputRef
  };
}
